<script lang="ts">
  // RevisionEditor — the full cube editor opened against an existing
  // proposal so the proposer can iterate after a REQUEST_CHANGES
  // review.
  //
  // Difference from CubeEditor (the new-proposal flow):
  //   - State is local (not the global staged-edits store) — no draft
  //     auto-save lifecycle, the revision is in-memory until Submit.
  //   - Initial staged edits are seeded from the proposal's existing
  //     deltas. The proposer sees the proposed values already staged
  //     and can adjust, remove, or add cells.
  //   - Submit calls api.resubmitProposal (commits onto the existing
  //     proposal branch); on success, navigate back to the proposal
  //     view. GitHub's `dismiss_stale_reviews: true` clears the prior
  //     CHANGES_REQUESTED and the PR re-enters the reviewer's queue.

  import { onMount } from 'svelte';
  import { api, ApiError } from '../lib/api';
  import { navigate } from '../lib/router.svelte';
  import type { StagedCellEdit } from '../lib/staged-edits';
  import type {
    CubeCellValue,
    EstablishmentFeeOverride,
    EstablishmentFeeOverrideTier,
    ProposalDetail,
    ProductCatalogueFile,
    ProposalReview
  } from '../lib/types';
  import { formatRate, formatAmount, formatCommentTimestamp, formatLocalDate, formatEffectiveTimestamp, formatClampedFromNote } from '../lib/formatters';
  import { findProduct } from '../lib/products';
  import CubeTable from './CubeTable.svelte';
  import SwipeToConfirm from './SwipeToConfirm.svelte';
  import ProposalTimeline from './ProposalTimeline.svelte';

  interface Props {
    prNumber: number;
  }

  const { prNumber }: Props = $props();

  let detail = $state<ProposalDetail | null>(null);
  let loading = $state<boolean>(true);
  let loadError = $state<string | null>(null);
  let submissionError = $state<string | null>(null);
  let submitting = $state<boolean>(false);

  // Local edits keyed by coordinate. Mirrors the StagedCellEdit shape
  // the global staged-edits store uses, but lives only for this
  // component's lifetime — closing the page discards the work.
  let edits = $state<Record<string, StagedCellEdit>>({});
  let revisionDescription = $state<string>('');

  // Per-credit-rating establishment-fee override revisions. Each entry
  // captures the proposal's prior override (`before`, from
  // detail.current) and the proposer's revised override (`staged`,
  // initially seeded from detail.candidate and editable via the inline
  // override editor below). `staged: null` means the proposer wants to
  // remove the override on this credit-rating. Submitting the revision
  // sends the array as `fee_override_edits` on the resubmit payload.
  interface OverrideRevision {
    credit_rating_id: string;
    before: EstablishmentFeeOverride | null;
    staged: EstablishmentFeeOverride | null;
  }
  let overrideRevisions = $state<Record<string, OverrideRevision>>({});

  // Inline override-editor state — mirrors CubeEditor's override editor.
  // Strings rather than numbers on each tier so empty inputs do not
  // coerce to NaN; commitOverrideRevise converts on save.
  interface OverrideEditorTier {
    min_amount: string;
    max_amount: string;
    fee: string;
  }
  let editingOverrideId = $state<string | null>(null);
  let editingOverrideBefore = $state<EstablishmentFeeOverride | null>(null);
  let editingOverrideTiers = $state<OverrideEditorTier[]>([]);
  let overrideError = $state<string | null>(null);

  // Inline-cell-editor state — same UX as the New Proposal cube
  // editor. A click on a cell sets editingCoord; the form inputs
  // hold tentative values until commit.
  let editingCoord = $state<string | null>(null);
  let editingBefore = $state<CubeCellValue | null>(null);
  let inputRate = $state<number | string | null>('');
  let inputAmount = $state<number | string | null>('');
  let rateInputEl: HTMLInputElement | null = $state(null);

  // Channel selector — same default as the cube-table view in the
  // Catalogue tab. The proposer can switch channels while revising.
  let channel = $state<string>('hLoan');

  // Reviewer-comment surface (INTERFACE-SPECIFICATION.md section 8.4).
  // The latest active review (CHANGES_REQUESTED or APPROVED) is pinned
  // at the top so the proposer can act on the feedback while editing.
  // Older reviews collapse behind [View earlier comments (N)].
  let earlierCommentsExpanded = $state<boolean>(false);

  // Discard-proposal action surface.
  let discardError = $state<string | null>(null);
  let discarding = $state<boolean>(false);

  onMount(() => {
    void load();
  });

  async function load() {
    loading = true;
    loadError = null;
    try {
      detail = await api.loadProposal(prNumber);
      if (detail) {
        seedEditsFromProposal(detail);
        seedOverrideRevisionsFromProposal(detail);
        // Seed channel from the proposal's first delta if any.
        const firstCoord = detail.deltas[0]?.coordinates;
        if (firstCoord) {
          const parts = firstCoord.split(' / ');
          const channelGuess = parts[parts.length - 1];
          if (channelGuess) channel = channelGuess;
        }
      }
    } catch (err) {
      loadError = err instanceof ApiError
        ? `${err.code}: ${err.message}`
        : err instanceof Error ? err.message : 'load_failed';
    } finally {
      loading = false;
    }
  }

  function seedOverrideRevisionsFromProposal(d: ProposalDetail) {
    // Diff detail.current vs detail.candidate at the
    // `rate_cube.{credit_rating_id}.establishment_fee_override` path.
    // Each differing credit-rating produces an OverrideRevision keyed
    // by id; the user can then open the inline editor on each to revise
    // or remove. Mirrors the server-side diffNonRateFields walk.
    const next: Record<string, OverrideRevision> = {};
    const currentCube = (d.current?.rate_cube ?? {}) as Record<string, { establishment_fee_override?: EstablishmentFeeOverride }>;
    const candidateCube = (d.candidate?.rate_cube ?? {}) as Record<string, { establishment_fee_override?: EstablishmentFeeOverride }>;
    const ids = new Set<string>([...Object.keys(currentCube), ...Object.keys(candidateCube)]);
    for (const id of ids) {
      const before = currentCube[id]?.establishment_fee_override ?? null;
      const staged = candidateCube[id]?.establishment_fee_override ?? null;
      if (JSON.stringify(before) === JSON.stringify(staged)) continue;
      next[id] = { credit_rating_id: id, before, staged };
    }
    overrideRevisions = next;
  }

  function handleOverrideRevise(creditRatingId: string) {
    if (!detail) return;
    const revision = overrideRevisions[creditRatingId];
    const seed = revision?.staged ?? revision?.before ?? null;
    editingOverrideId = creditRatingId;
    editingOverrideBefore = revision?.before ?? null;
    overrideError = null;
    if (seed && Array.isArray(seed.tiers) && seed.tiers.length > 0) {
      editingOverrideTiers = seed.tiers.map((t) => ({
        min_amount: String(t.min_amount),
        max_amount: String(t.max_amount),
        fee: String(t.fee)
      }));
    } else {
      const max = detail.current?.loan_amount_range?.max ?? 100000;
      editingOverrideTiers = [{ min_amount: '0', max_amount: String(max), fee: '0' }];
    }
  }

  function closeOverrideEditor() {
    editingOverrideId = null;
    editingOverrideBefore = null;
    editingOverrideTiers = [];
    overrideError = null;
  }

  function addOverrideTier() {
    if (!detail) return;
    const last = editingOverrideTiers[editingOverrideTiers.length - 1];
    const lastMax = last ? Number(last.max_amount) : 0;
    const max = detail.current?.loan_amount_range?.max ?? 100000;
    editingOverrideTiers = [
      ...editingOverrideTiers,
      { min_amount: String(Number.isFinite(lastMax) ? lastMax + 1 : 0), max_amount: String(max), fee: '0' }
    ];
  }

  function removeOverrideTier(idx: number) {
    editingOverrideTiers = editingOverrideTiers.filter((_, i) => i !== idx);
    if (editingOverrideTiers.length === 0) {
      const max = detail?.current?.loan_amount_range?.max ?? 100000;
      editingOverrideTiers = [{ min_amount: '0', max_amount: String(max), fee: '0' }];
    }
  }

  const overrideTierValidation = $derived.by(() => {
    const errors: string[] = [];
    const warnings: string[] = [];
    const tiersForCheck: { idx: number; min: number; max: number; fee: number; valid: boolean }[] = [];
    for (let i = 0; i < editingOverrideTiers.length; i += 1) {
      const tier = editingOverrideTiers[i]!;
      const min = Number(tier.min_amount);
      const max = Number(tier.max_amount);
      const fee = Number(tier.fee);
      if (!Number.isFinite(min) || !Number.isFinite(max) || !Number.isFinite(fee)) {
        errors.push(`Tier ${i + 1}: min, max, and fee must all be numbers.`);
        tiersForCheck.push({ idx: i, min, max, fee, valid: false });
        continue;
      }
      if (min < 0 || max < 0 || fee < 0) errors.push(`Tier ${i + 1}: values must be non-negative.`);
      if (min > max) errors.push(`Tier ${i + 1}: min ($${min}) cannot exceed max ($${max}).`);
      tiersForCheck.push({ idx: i, min, max, fee, valid: min <= max && min >= 0 && max >= 0 && fee >= 0 });
    }
    const sorted = tiersForCheck
      .filter((t) => t.valid)
      .sort((a, b) => a.min - b.min);
    for (let i = 1; i < sorted.length; i += 1) {
      const prev = sorted[i - 1]!;
      const curr = sorted[i]!;
      if (curr.min <= prev.max) {
        errors.push(`Tier ${prev.idx + 1} ($${prev.min}–$${prev.max}) overlaps tier ${curr.idx + 1} ($${curr.min}–$${curr.max}).`);
      } else if (curr.min !== prev.max + 1) {
        warnings.push(`Tier boundaries $${prev.max} and $${curr.min} are not adjacent integers — loan amounts $${prev.max + 1}–$${curr.min - 1} fall back to the product-level establishment-fee tier.`);
      }
    }
    return { errors, warnings };
  });

  function commitOverrideRevise() {
    if (!editingOverrideId) return;
    if (overrideTierValidation.errors.length > 0) {
      overrideError = overrideTierValidation.errors[0]!;
      return;
    }
    const parsed: EstablishmentFeeOverrideTier[] = editingOverrideTiers.map((tier) => ({
      min_amount: Number(tier.min_amount),
      max_amount: Number(tier.max_amount),
      fee: Number(tier.fee)
    }));
    const stagedOverride: EstablishmentFeeOverride = { tiers: parsed };
    const before = editingOverrideBefore;
    if (JSON.stringify(before) === JSON.stringify(stagedOverride)) {
      // Revising to the original drops the revision entirely.
      const { [editingOverrideId]: _removed, ...rest } = overrideRevisions;
      overrideRevisions = rest;
    } else {
      overrideRevisions = {
        ...overrideRevisions,
        [editingOverrideId]: {
          credit_rating_id: editingOverrideId,
          before,
          staged: stagedOverride
        }
      };
    }
    closeOverrideEditor();
  }

  function stageOverrideRemovalAtCreditRating() {
    if (!editingOverrideId) return;
    const before = editingOverrideBefore;
    if (before === null) {
      // No override existed and the proposer wants none — drop the
      // revision entirely so the proposal carries the original state.
      const { [editingOverrideId]: _removed, ...rest } = overrideRevisions;
      overrideRevisions = rest;
    } else {
      overrideRevisions = {
        ...overrideRevisions,
        [editingOverrideId]: {
          credit_rating_id: editingOverrideId,
          before,
          staged: null
        }
      };
    }
    closeOverrideEditor();
  }

  function dropOverrideRevision(creditRatingId: string) {
    const { [creditRatingId]: _removed, ...rest } = overrideRevisions;
    overrideRevisions = rest;
  }

  function describeOverride(override: EstablishmentFeeOverride | null): string {
    if (!override || !Array.isArray(override.tiers) || override.tiers.length === 0) return '—';
    if (override.tiers.length === 1) {
      const fee = override.tiers[0]?.fee;
      return typeof fee === 'number' ? formatAmount(fee) : '—';
    }
    return `varies (${override.tiers.length} tiers)`;
  }

  function seedEditsFromProposal(d: ProposalDetail) {
    const next: Record<string, StagedCellEdit> = {};
    // detail.deltas comes through as { coordinates, previous, next }
    // (the diffCubes shape). Each entry's `next` is a CubeCellValue
    // with both interest_rate_pa and max_loan_amount fields; we only
    // include fields whose value differs from `previous` so the
    // resulting cube_edits payload doesn't redundantly assert values
    // that are unchanged.
    for (const delta of d.deltas) {
      const staged: Partial<CubeCellValue> = {};
      // Workaround: SPA's CellDelta type declares `before/after` but
      // the actual server shape is `previous/next`. Read either.
      const prev = (delta as unknown as { previous?: CubeCellValue }).previous;
      const after = (delta as unknown as { next?: CubeCellValue }).next ?? null;
      if (after && prev) {
        if (after.interest_rate_pa !== undefined && after.interest_rate_pa !== prev.interest_rate_pa) {
          staged.interest_rate_pa = after.interest_rate_pa;
        }
        if (after.max_loan_amount !== undefined && after.max_loan_amount !== prev.max_loan_amount) {
          staged.max_loan_amount = after.max_loan_amount;
        }
      } else if (after) {
        if (after.interest_rate_pa !== undefined) staged.interest_rate_pa = after.interest_rate_pa;
        if (after.max_loan_amount !== undefined) staged.max_loan_amount = after.max_loan_amount;
      }
      if (Object.keys(staged).length > 0 && prev) {
        next[delta.coordinates] = {
          coordinates: delta.coordinates,
          before: prev,
          staged
        };
      }
    }
    edits = next;
  }

  // The "main" catalogue passed to CubeTable. The table renders each
  // cell at its main value, with the staged-edits overlay (the
  // stagedByCoordinate prop) producing the "before → after" arrow.
  // Critical: we pass detail.current here, NOT a candidate that
  // already has the proposal's edits applied — if we passed the
  // candidate, the cell's stored value would equal the staged value
  // and the arrow would render as "5.35 → 5.35" instead of
  // "5.76 → 5.35". The New Proposal flow's CubeEditor does the same:
  // catalogue prop is current, stagedByCoordinate is the overlay.
  const baselineCube = $derived<ProductCatalogueFile | null>(
    detail?.current ?? null
  );

  const stagedByCoordinate = $derived<Record<string, Partial<CubeCellValue>>>(
    Object.fromEntries(Object.entries(edits).map(([coord, edit]) => [coord, edit.staged]))
  );

  $effect(() => {
    if (editingCoord && rateInputEl) {
      rateInputEl.focus();
      rateInputEl.select();
    }
  });

  function handleCellClick(coordinates: string, before: CubeCellValue) {
    editingCoord = coordinates;
    editingBefore = before;
    const existing = edits[coordinates]?.staged;
    inputRate = existing?.interest_rate_pa ?? before.interest_rate_pa ?? null;
    inputAmount = existing?.max_loan_amount ?? before.max_loan_amount ?? null;
  }

  function closeInlineEditor() {
    editingCoord = null;
    editingBefore = null;
  }

  function commitInline(field: 'interest_rate_pa' | 'max_loan_amount') {
    if (!editingCoord || !editingBefore) return;
    const raw = field === 'interest_rate_pa' ? inputRate : inputAmount;
    if (raw === null || raw === undefined || raw === '') return;
    const value = typeof raw === 'number' ? raw : Number(String(raw).trim());
    if (!Number.isFinite(value)) return;
    if (value === editingBefore[field]) {
      // No-op: matching before drops the cell from the revision for
      // this field. If the cell has no other staged field either, also
      // remove the whole entry.
      const existing = edits[editingCoord];
      if (existing) {
        const stagedCopy: Partial<CubeCellValue> = { ...existing.staged };
        delete stagedCopy[field];
        if (Object.keys(stagedCopy).length === 0) {
          const { [editingCoord]: _removed, ...rest } = edits;
          edits = rest;
        } else {
          edits = { ...edits, [editingCoord]: { ...existing, staged: stagedCopy } };
        }
      }
      return;
    }
    const existing = edits[editingCoord];
    const merged: StagedCellEdit = existing
      ? {
          coordinates: editingCoord,
          before: existing.before,
          staged: { ...existing.staged, [field]: value }
        }
      : {
          coordinates: editingCoord,
          before: editingBefore,
          staged: { [field]: value },
          field
        };
    edits = { ...edits, [editingCoord]: merged };
  }

  function removeEdit(coord: string) {
    const { [coord]: _removed, ...rest } = edits;
    edits = rest;
  }

  async function submitRevision() {
    if (!detail) return;
    const valueType = detail.value_type ?? 'rates';
    const payload: {
      cube_edits?: Array<{ coordinates: string; new_value: { interest_rate_pa?: number; max_loan_amount?: number } }>;
      fee_override_edits?: Array<{ credit_rating_id: string; new_value: EstablishmentFeeOverride | null }>;
    } = {};

    if (valueType === 'rates') {
      const cube_edits: Array<{
        coordinates: string;
        new_value: { interest_rate_pa?: number; max_loan_amount?: number };
      }> = [];
      for (const edit of Object.values(edits)) {
        const new_value: { interest_rate_pa?: number; max_loan_amount?: number } = {};
        if (typeof edit.staged.interest_rate_pa === 'number') {
          new_value.interest_rate_pa = edit.staged.interest_rate_pa;
        }
        if (typeof edit.staged.max_loan_amount === 'number') {
          new_value.max_loan_amount = edit.staged.max_loan_amount;
        }
        if (Object.keys(new_value).length > 0) {
          cube_edits.push({ coordinates: edit.coordinates, new_value });
        }
      }
      if (cube_edits.length === 0) {
        submissionError = 'A revision must have at least one cell change. Close this revision to keep the proposal as-is.';
        return;
      }
      payload.cube_edits = cube_edits;
    } else if (valueType === 'fees') {
      const fee_override_edits = Object.values(overrideRevisions).map((rev) => ({
        credit_rating_id: rev.credit_rating_id,
        new_value: rev.staged
      }));
      if (fee_override_edits.length === 0) {
        submissionError = 'A revision must have at least one override change. Close this revision to keep the proposal as-is.';
        return;
      }
      payload.fee_override_edits = fee_override_edits;
    } else {
      submissionError = `Revise is not yet supported for value-type "${valueType}". Discard the proposal and start a fresh draft to make changes.`;
      return;
    }

    submitting = true;
    submissionError = null;
    try {
      await api.resubmitProposal(prNumber, payload, revisionDescription.trim() || undefined);
      navigate(`/changes/pending-review/${prNumber}`);
    } catch (err) {
      submissionError = err instanceof ApiError
        ? `${err.code}: ${err.message}`
        : err instanceof Error ? err.message : 'resubmit_failed';
    } finally {
      submitting = false;
    }
  }

  function cancelRevision() {
    navigate(`/changes/pending-review/${prNumber}`);
  }

  async function discardProposal() {
    if (!detail || typeof window === 'undefined') return;
    const confirmed = window.confirm(
      `Discard proposal #${detail.pr_number}?\n\n` +
      'The PR will close without merging and will appear in Recently ' +
      'Completed with a Closed badge. The change will not become live. ' +
      'This action cannot be undone — to propose the change again, start ' +
      'a fresh draft.'
    );
    if (!confirmed) return;
    const reason = window.prompt(
      'Reason for discarding (optional, recorded on the PR thread for audit):',
      ''
    );
    discardError = null;
    discarding = true;
    try {
      await api.discardProposal(prNumber, reason || undefined);
      navigate(`/changes/recently-completed/${prNumber}`);
    } catch (err) {
      discardError = err instanceof ApiError
        ? `${err.code}: ${err.message}`
        : err instanceof Error ? err.message : 'discard_failed';
    } finally {
      discarding = false;
    }
  }

  function productLabel(d: ProposalDetail): string {
    const desc = findProduct(d.jurisdiction, d.product);
    return desc ? `${desc.label} · ${desc.customerLabel}` : `${d.jurisdiction}/${d.product}`;
  }

  function reviewVerdictLabel(state: string): string {
    if (state === 'CHANGES_REQUESTED') return 'Changes requested';
    if (state === 'APPROVED') return 'Approved';
    if (state === 'DISMISSED') return 'Dismissed';
    if (state === 'COMMENTED') return 'Commented';
    if (state === 'PENDING') return 'Pending';
    return state;
  }

  // Latest active review (CHANGES_REQUESTED or APPROVED, ignoring
  // dismissed / commented / pending) — pinned at the top of the
  // Revise interface per spec section 8.4. The full proposal history
  // (every state-changing action: opens, revisions, comments,
  // approvals, merges, queue actions) renders below via the
  // ProposalTimeline component.
  const sortedReviews = $derived<ProposalReview[]>(
    detail?.reviews
      ? detail.reviews.slice().sort((a, b) => (a.submitted_at < b.submitted_at ? 1 : -1))
      : []
  );
  const pinnedReview = $derived<ProposalReview | null>(
    sortedReviews.find((r) => r.state === 'CHANGES_REQUESTED' || r.state === 'APPROVED') ?? null
  );

  // The Resubmit button enables when the user has staged at least one
  // revision in the value-type-appropriate bucket. For rates revisions,
  // that is the cube-cell edits map; for fees revisions, the override-
  // revisions map.
  const hasRevisions = $derived<boolean>(
    detail?.value_type === 'fees'
      ? Object.keys(overrideRevisions).length > 0
      : Object.keys(edits).length > 0
  );
</script>

<section>
  {#if loading}
    <p class="muted">Loading proposal…</p>
  {:else if loadError}
    <div class="banner banner-fail">{loadError}</div>
  {:else if !detail}
    <p class="muted">Proposal not found.</p>
  {:else}
    <header class="revise-header">
      <h2>Revise: {productLabel(detail)} <span class="revise-header__ref">[#{detail.pr_number} · {detail.branch}]</span></h2>
      <p class="muted">{detail.change_description}</p>
      <dl class="revise-meta">
        <div><dt>Proposed by</dt><dd>{detail.proposed_by} · {detail.created_at ? formatLocalDate(detail.created_at) : '—'}</dd></div>
        <div><dt>Reviewer</dt><dd>{detail.reviewer ?? '—'}</dd></div>
        <div>
          <dt>Effective-from</dt>
          <dd>
            {formatEffectiveTimestamp(detail.effective_from)}
            {#if detail.proposed_effective_from}
              <div class="clamp-note muted">{formatClampedFromNote(detail.proposed_effective_from)}</div>
            {/if}
          </dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>
            {#if pinnedReview}
              {reviewVerdictLabel(pinnedReview.state)}
              <span class="muted">(most recent review: {formatCommentTimestamp(pinnedReview.submitted_at)})</span>
            {:else}
              No active review yet
            {/if}
          </dd>
        </div>
      </dl>
    </header>

    {#if pinnedReview}
      <!-- INTERFACE-SPECIFICATION.md section 8.4: the most recent active
           reviewer comment is pinned at the top so the proposer can act
           on the feedback while editing. The comment does not collapse
           on scroll. -->
      <aside class="pinned-review">
        <header>
          <strong>{pinnedReview.user?.login ?? 'reviewer'}</strong>
          <span class="muted">·</span>
          <span class="muted">{formatCommentTimestamp(pinnedReview.submitted_at)}</span>
          <span class="muted">·</span>
          <span class="pinned-review__verdict">{reviewVerdictLabel(pinnedReview.state)}</span>
        </header>
        {#if pinnedReview.body}
          <p class="pinned-review__body">{pinnedReview.body}</p>
        {:else}
          <p class="pinned-review__body muted">(No comment body — verdict only.)</p>
        {/if}
        <button
          class="btn-link pinned-review__expand"
          type="button"
          onclick={() => (earlierCommentsExpanded = !earlierCommentsExpanded)}
        >
          {earlierCommentsExpanded ? 'Hide full proposal history' : 'View full proposal history'}
        </button>
        {#if earlierCommentsExpanded}
          <ProposalTimeline {prNumber} />
        {/if}
      </aside>
    {/if}

    <p class="advisory">
      <strong>Heads up:</strong> Resubmit commits these values onto the
      existing proposal branch — no new PR is created. The reviewer's
      prior <code>CHANGES_REQUESTED</code> review is auto-dismissed by
      branch protection on the new commit, and the PR re-enters their
      queue. The change ID and PR number are preserved across rounds.
    </p>

    {#if detail.value_type === 'rates' || !detail.value_type}
      {#if (detail.candidate?._dimension_refs?.pricing_model === 'bureau_band_x_housing_x_channel') }
        <label class="channel-selector">
          <span class="muted">Channel</span>
          <select bind:value={channel}>
            <option value="hLoan">hLoan</option>
            <option value="hMoney">hMoney</option>
          </select>
        </label>
      {/if}

      {#if baselineCube}
        <CubeTable
          catalogue={baselineCube}
          {channel}
          onCellClick={handleCellClick}
          {stagedByCoordinate}
        />
      {/if}
    {:else if detail.value_type === 'fees'}
      <!-- Fees-revise mode. Surface every credit-rating-axis-level
           override the proposal touched. Each row opens the inline
           override editor below — same B-lite UX as the new-proposal
           flow (single-tier shorthand inline, multi-tier in a tier
           table). The `before` column shows the catalogue's current
           value, the `after` column shows the proposed value the user
           is revising. -->
      <section class="override-revise-panel">
        <h3 class="section-heading">Proposed establishment-fee overrides</h3>
        {#if Object.keys(overrideRevisions).length === 0}
          <p class="muted">This proposal touches no per-credit-rating establishment-fee overrides.</p>
        {:else}
          <table class="cube-table">
            <thead>
              <tr>
                <th>Credit rating</th>
                <th>Before (current in-effect)</th>
                <th>Proposed (revised)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {#each Object.values(overrideRevisions) as revision (revision.credit_rating_id)}
                <tr>
                  <td><code>rate_cube.{revision.credit_rating_id}.establishment_fee_override</code></td>
                  <td>{describeOverride(revision.before)}</td>
                  <td>{describeOverride(revision.staged)}</td>
                  <td>
                    <button class="btn-link" type="button" onclick={() => handleOverrideRevise(revision.credit_rating_id)}>Edit</button>
                    <button class="btn-link" type="button" onclick={() => dropOverrideRevision(revision.credit_rating_id)}>Drop revision</button>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
          <p class="muted">"Drop revision" removes the override from the revision so the proposal carries the catalogue's current value (no override change on this credit-rating).</p>
        {/if}
      </section>
    {:else}
      <div class="banner banner-warn">
        <strong>Revise not yet supported for value-type "{detail.value_type}".</strong>
        <p class="muted">Discard the proposal and start a fresh draft to make changes. Support for revising amounts, purposes, and terms is queued for a future PR.</p>
      </div>
    {/if}

    {#if editingCoord}
      <div class="cell-editor banner">
        <header>
          <strong>Editing</strong>
          <code>{editingCoord}</code>
          <button class="btn-link" type="button" onclick={closeInlineEditor} aria-label="Close cell editor">Close</button>
        </header>
        <div class="cell-editor__row">
          <label>
            <span class="muted">Interest rate (%)</span>
            <input
              type="number"
              step="0.01"
              bind:value={inputRate}
              bind:this={rateInputEl}
              onblur={() => commitInline('interest_rate_pa')}
              onkeydown={(e) => { if (e.key === 'Enter') { commitInline('interest_rate_pa'); closeInlineEditor(); } }}
            />
          </label>
          <label>
            <span class="muted">Max loan amount ($)</span>
            <input
              type="number"
              step="1"
              bind:value={inputAmount}
              onblur={() => commitInline('max_loan_amount')}
              onkeydown={(e) => { if (e.key === 'Enter') { commitInline('max_loan_amount'); closeInlineEditor(); } }}
            />
          </label>
        </div>
        <p class="muted">Press Enter or click outside to stage. Setting a value back to the original drops that field from the revision.</p>
      </div>
    {/if}

    {#if editingOverrideId}
      <div class="cell-editor banner override-editor">
        <header>
          <strong>Editing establishment-fee override</strong>
          <code>rate_cube.{editingOverrideId}.establishment_fee_override</code>
          <button class="btn-link" type="button" onclick={closeOverrideEditor} aria-label="Close override editor">Close</button>
        </header>
        <p class="muted">
          The override applies to every cube cell under credit-rating <code>{editingOverrideId}</code>. A single tier
          covering the full loan-amount range renders inline as a scalar dollar figure; additional tiers introduce
          per-loan-amount fee variation within this credit-rating row.
        </p>
        <table class="override-tier-table cube-table">
          <thead>
            <tr>
              <th class="num">Min amount ($)</th>
              <th class="num">Max amount ($)</th>
              <th class="num">Fee ($)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {#each editingOverrideTiers as tier, idx (idx)}
              <tr>
                <td class="num"><input type="number" step="1" min="0" bind:value={tier.min_amount} /></td>
                <td class="num"><input type="number" step="1" min="0" bind:value={tier.max_amount} /></td>
                <td class="num"><input type="number" step="1" min="0" bind:value={tier.fee} /></td>
                <td>
                  {#if editingOverrideTiers.length > 1}
                    <button class="btn-link" type="button" onclick={() => removeOverrideTier(idx)}>Remove tier</button>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
        <div class="override-editor__actions">
          <button class="btn btn-link" type="button" onclick={addOverrideTier}>+ Add tier</button>
          <div class="override-editor__commit">
            <button class="btn btn-link" type="button" onclick={stageOverrideRemovalAtCreditRating}>Remove override</button>
            <button class="btn" type="button" onclick={closeOverrideEditor}>Cancel</button>
            <button class="btn btn-primary" type="button" onclick={commitOverrideRevise} disabled={overrideTierValidation.errors.length > 0}>Stage revision</button>
          </div>
        </div>
        {#if overrideTierValidation.errors.length > 0}
          <ul class="banner banner-warn override-editor__errors">
            {#each overrideTierValidation.errors as error (error)}
              <li>{error}</li>
            {/each}
          </ul>
        {/if}
        {#if overrideTierValidation.warnings.length > 0}
          <ul class="banner override-editor__warnings">
            {#each overrideTierValidation.warnings as warning (warning)}
              <li>{warning}</li>
            {/each}
          </ul>
        {/if}
        {#if overrideError && overrideTierValidation.errors.length === 0}
          <p class="banner banner-warn">{overrideError}</p>
        {/if}
      </div>
    {/if}

    {#if detail.value_type === 'rates' || !detail.value_type}
    <section class="revise-panel">
      <h3 class="section-heading">Revised changes ({Object.keys(edits).length})</h3>
      {#if Object.keys(edits).length === 0}
        <p class="muted">No cells staged. Click a cube cell to stage a revision.</p>
      {:else}
        <table class="cube-table">
          <thead>
            <tr>
              <th>Coordinates</th>
              <th class="num">% before</th>
              <th class="num">% after</th>
              <th class="num">$ before</th>
              <th class="num">$ after</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {#each Object.values(edits) as edit (edit.coordinates)}
              <tr>
                <td><code>{edit.coordinates}</code></td>
                <td class="num">{formatRate(edit.before.interest_rate_pa)}</td>
                <td class="num">{edit.staged.interest_rate_pa !== undefined ? formatRate(edit.staged.interest_rate_pa) : formatRate(edit.before.interest_rate_pa)}</td>
                <td class="num">{formatAmount(edit.before.max_loan_amount)}</td>
                <td class="num">{edit.staged.max_loan_amount !== undefined ? formatAmount(edit.staged.max_loan_amount) : formatAmount(edit.before.max_loan_amount)}</td>
                <td><button class="btn-link" type="button" onclick={() => removeEdit(edit.coordinates)}>Remove</button></td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </section>
    {/if}

    <label class="revision-description">
      <span>Revision description (optional)</span>
      <textarea
        rows="2"
        bind:value={revisionDescription}
        placeholder="e.g. Reduced 950+ HomeOwner per Dan's feedback"
      ></textarea>
    </label>

    {#if submissionError}
      <div class="banner banner-fail">{submissionError}</div>
    {/if}
    {#if discardError}
      <div class="banner banner-fail">{discardError}</div>
    {/if}

    <footer class="revise-footer">
      <button class="btn" type="button" onclick={cancelRevision} disabled={submitting || discarding}>Cancel</button>
      <div class="desktop-only">
        <button
          class="btn btn-discard"
          type="button"
          onclick={discardProposal}
          disabled={submitting || discarding}
        >
          {discarding ? 'Discarding…' : 'Discard proposal'}
        </button>
      </div>
      <div class="mobile-only revise-footer__discard">
        <SwipeToConfirm
          label="Discard proposal"
          confirmingLabel="Discarding…"
          disabled={submitting}
          confirming={discarding}
          onConfirm={discardProposal}
        />
      </div>
      <button
        class="btn btn-primary"
        type="button"
        onclick={submitRevision}
        disabled={submitting || discarding || !hasRevisions}
      >
        {submitting ? 'Resubmitting…' : 'Resubmit for Review →'}
      </button>
    </footer>
  {/if}
</section>

<style>
  .revise-header h2 { margin-bottom: var(--space-1); }
  .revise-header__ref {
    font-family: var(--font-mono);
    font-size: var(--type-sm);
    color: var(--surface-muted);
    font-weight: 400;
  }
  .revise-meta {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: var(--space-3);
    margin: var(--space-3) 0;
  }
  .revise-meta dt { font-size: var(--type-sm); color: var(--surface-muted); }
  .revise-meta dd { margin: 0; font-size: var(--type-sm); }
  /* Pinned reviewer comment (INTERFACE-SPECIFICATION.md section 8.4).
   * Sticky positioning keeps the most recent feedback visible while
   * the proposer scrolls through the cube editor below. */
  .pinned-review {
    position: sticky;
    top: 0;
    z-index: 5;
    background: var(--accent-warn-bg, var(--surface-card));
    border: 1px solid var(--accent-warn, var(--surface-line));
    border-radius: var(--radius-md);
    padding: var(--space-3);
    margin: var(--space-3) 0;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  }
  .pinned-review > header {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    align-items: baseline;
    font-size: var(--type-sm);
  }
  .pinned-review__verdict {
    font-weight: 600;
    text-transform: uppercase;
    font-size: var(--type-xs);
    letter-spacing: 0.04em;
    color: var(--accent-warn);
  }
  .pinned-review__body {
    margin: var(--space-2) 0 0 0;
    font-size: var(--type-sm);
    white-space: pre-wrap;
  }
  .pinned-review__expand {
    margin-top: var(--space-2);
    font-size: var(--type-sm);
  }
  .btn-discard {
    border-color: var(--accent-warn, var(--surface-line));
    color: var(--accent-warn, inherit);
  }
  .advisory {
    background: var(--surface-card);
    border-left: 3px solid var(--surface-line);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    font-size: var(--type-sm);
    margin: var(--space-3) 0;
  }
  .channel-selector {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    margin-bottom: var(--space-3);
  }
  .cell-editor {
    margin-top: var(--space-3);
    background: var(--surface-card);
  }
  .cell-editor header {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin-bottom: var(--space-2);
  }
  .cell-editor__row {
    display: flex;
    gap: var(--space-3);
    flex-wrap: wrap;
  }
  .cell-editor input {
    width: 12rem;
    padding: var(--space-1) var(--space-2);
    border: 1px solid var(--surface-line);
    border-radius: var(--radius-sm);
  }
  .revise-panel { margin-top: var(--space-5); }
  .revision-description {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    margin-top: var(--space-3);
  }
  .revision-description textarea {
    width: 100%;
    padding: var(--space-2);
    border: 1px solid var(--surface-line);
    border-radius: var(--radius-sm);
  }
  .revise-footer {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-3);
    margin-top: var(--space-4);
    padding-top: var(--space-3);
    border-top: 1px solid var(--surface-line-soft);
  }
  code { font-family: var(--font-mono); }
</style>
